// Versão: 1.00
// Data..: 2026-09-24
// Sistema de notificações (toasts) de sucesso/erro/info.

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

type ToastKind = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  notify: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  notify: () => {},
  success: () => {},
  error: () => {},
});

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, kind: ToastKind = 'info') => {
      const id = ++counter;
      setToasts((list) => [...list, { id, kind, message }]);
      // Remove automaticamente após 4 segundos.
      setTimeout(() => remove(id), 4000);
    },
    [remove],
  );

  const success = useCallback((m: string) => notify(m, 'success'), [notify]);
  const error = useCallback((m: string) => notify(m, 'error'), [notify]);

  const icon: Record<ToastKind, string> = { success: '✓', error: '✕', info: 'ℹ' };
  const color: Record<ToastKind, string> = {
    success: 'var(--success)',
    error: 'var(--danger)',
    info: 'var(--info)',
  };

  return (
    <ToastContext.Provider value={{ notify, success, error }}>
      {children}
      <div
        style={{
          position: 'fixed', top: 16, right: 16, zIndex: 1000,
          display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360,
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className="fade-in"
            onClick={() => remove(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderLeft: `3px solid ${color[t.kind]}`,
              borderRadius: 'var(--radius-sm)', padding: '12px 14px',
              boxShadow: 'var(--shadow-lg)', cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                background: color[t.kind], color: '#fff',
                display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700,
              }}
            >
              {icon[t.kind]}
            </span>
            <span className="text-sm">{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
